import './tracking.js';
import compare from '../../../../Assets/scripts/api/compare/compare.js';
import cancelCompare from '../../../../Assets/scripts/api/compare/cancel-compare.js';
import getCompareItems from '../../../../Assets/scripts/api/compare/get-compare-items.js';
import findCompareItemFromList from '../../../../Assets/scripts/api/compare/find-compare-item-from-list.js';
import getProductDataFromAttributes from '../../../../Assets/helpers/tracking/get-product-data-from-attributes.js';

/** @typedef {import('../../../../Assets/scripts/api/compare/typedefs.js').CompareItem} CompareItem */

/**
 * @fires compareitemcancel
 * @fires beforecompareitem
 * @fires compareitem
 * @fires beforecomparecancel
 */
export default class CompareCheckbox extends HTMLElement {
  #checkbox;

  constructor() {
    super();

    this.#checkbox = this.querySelector('input[type="checkbox"]');

    // Event listeners
    this.#checkbox.addEventListener('change', this.#handleChange.bind(this));
    this.#checkbox.addEventListener('click', (e) => e.stopPropagation());
    this.#checkbox.addEventListener('keydown', this.#handleKeydown.bind(this));
    globalThis.addEventListener('compare', this.#handleCompare.bind(this));

    // Initial state
    const { items } = getCompareItems();
    this.#updateState(findCompareItemFromList(items, this.prid, this.catalog) !== null);
  }

  get prid() {
    return this.dataset.prid;
  }

  get catalog() {
    return this.dataset.catalog;
  }

  get type() {
    return this.dataset.type || null;
  }

  // ========== Event Handlers ==========

  #handleChange(event) {
    event.stopPropagation();

    const items = [
      {
        ...getProductDataFromAttributes(this),
        offer: null,
        type: this.type
      }
    ];

    if (this.#checkbox.checked) {
      // Adding to compare
      const beforeEvent = new CustomEvent('beforecompareitem', {
        bubbles: true,
        cancelable: true,
        detail: { items, relatedTarget: this }
      });

      if (!this.dispatchEvent(beforeEvent)) {
        this.#checkbox.checked = false;
        return;
      }

      try {
        compare(...items);
      } catch (error) {
        this.#checkbox.checked = false;
        this.dispatchEvent(
          new CustomEvent('compareerror', {
            bubbles: true,
            detail: { error, relatedTarget: this }
          })
        );
        return;
      }

      // Notify item has been added
      this.dispatchEvent(
        new CustomEvent('compareitem', {
          bubbles: true,
          detail: { items, relatedTarget: this }
        })
      );
    } else {
      // Removing from compare
      const beforeEvent = new CustomEvent('beforecomparecancel', {
        bubbles: true,
        cancelable: true,
        detail: { items, relatedTarget: this }
      });

      if (!this.dispatchEvent(beforeEvent)) {
        this.#checkbox.checked = true;
        return;
      }

      cancelCompare(...items);

      // Notify item has been removed
      this.dispatchEvent(
        new CustomEvent('compareitemcancel', {
          bubbles: true,
          detail: { items, relatedTarget: this }
        })
      );
    }

    // Notify everyone that compare items changed
    globalThis.dispatchEvent(
      new CustomEvent('compare', {
        bubbles: true,
        detail: getCompareItems()
      })
    );
  }

  #handleKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      this.#checkbox.checked = !this.#checkbox.checked;
      this.#checkbox.dispatchEvent(new Event('change'));
    }
  }

  #handleCompare({ detail: { items } }) {
    this.#updateState(findCompareItemFromList(items, this.prid, this.catalog) !== null);
  }

  // ========== State Management ==========

  #updateState(added) {
    this.#checkbox.checked = added;
    this.classList.toggle('--added', added);

    const label = added ? 'Retirer du comparateur' : 'Ajouter au comparateur';
    this.#checkbox.setAttribute('aria-label', label);
    this.#checkbox.title = label;
  }
}

customElements.get('f-compare-checkbox') || customElements.define('f-compare-checkbox', CompareCheckbox);
